import React, { useState, useRef, useEffect } from 'react';
import { 
  User, Lock, Eye, EyeOff, ArrowRight, Loader2, 
  CheckCircle, AlertCircle, MapPin
} from 'lucide-react';




const LoginForm = ({ onLogin, loading }) => {
  // State & Refs
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [isFetchingGps, setIsFetchingGps] = useState(false);


  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0, shineX: 50, shineY: 50 });


  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -5.0;
    const rotateY = ((x - centerX) / centerX) * 5.0;

    setTilt({
      x: rotateX,
      y: rotateY,
      shineX: (x / rect.width) * 100,
      shineY: (y / rect.height) * 100
    });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0, shineX: 50, shineY: 50 });
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsFetchingGps(true);

    try {
      const getLocation = () => new Promise((resolve) => {
        if (!navigator.geolocation) {
          resolve(null);
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            resolve({
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              timestamp: pos.timestamp || Date.now()
            });
          },
          (err) => {
            console.warn("Location prompt dismissed or blocked:", err.message);
            resolve(null);
          },
          { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
        );
      });

      const locationData = await getLocation();

      const finalLoginData = {
        username: loginData.username.trim(),
        password: loginData.password,
        location: locationData
      };

      const result = await onLogin(finalLoginData);
      if (!result?.success) {
        setError(result?.error || "Login failed. Please verify credentials.");
      }
    } catch (err) {
      setError("An unexpected authentication error occurred.");
    } finally {
      setIsFetchingGps(false);
    }
  };

  const isSubmitDisabled = loading || isFetchingGps;

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#0B0602] text-white selection:bg-[#F59E0B] selection:text-black overflow-hidden relative font-sans" data-testid="login-page">
      
      
      <DynamicFireworks />

      {/* Inject Keyframe Styles */}
      <style>{STYLE_SHEET}</style>

      <div className="absolute top-[-10%] left-[-5%] w-[650px] h-[650px] bg-gradient-to-br from-amber-500/30 via-yellow-600/20 to-transparent rounded-full blur-[140px] pointer-events-none mix-blend-screen animate-pulse duration-[10000ms]" />
      <div className="absolute top-[5%] right-[5%] w-[550px] h-[550px] bg-gradient-to-bl from-amber-600/30 via-orange-500/20 to-transparent rounded-full blur-[130px] pointer-events-none mix-blend-screen" />
      <div className="absolute bottom-[-15%] right-[20%] w-[650px] h-[650px] bg-gradient-to-tl from-yellow-700/30 via-amber-900/25 to-transparent rounded-full blur-[150px] pointer-events-none mix-blend-screen" />

      {/* Golden Festive Header */}
      <header className="w-full z-30 pt-3 pb-2.5 px-4 sm:px-8 bg-gradient-to-r from-[#180A02]/90 via-[#2C1504]/80 to-[#180A02]/90 border-b border-amber-500/35 backdrop-blur-md shadow-[0_4px_30px_rgba(245,158,11,0.25)] flex items-center justify-between">
        
        <div className="flex items-center gap-2" />

        <div className="flex items-center gap-2.5 sm:gap-4 animate-in fade-in duration-700">
          <DiyaIcon className="w-6 h-6 sm:w-7 sm:h-7 filter drop-shadow-[0_0_12px_rgba(245,158,11,0.95)]" />
          <h2 className="text-base sm:text-xl md:text-2xl font-extrabold tracking-widest uppercase text-transparent bg-clip-text bg-gradient-to-r from-amber-100 via-amber-300 to-yellow-200 drop-shadow-[0_2px_14px_rgba(245,158,11,0.9)] text-center" style={{ fontFamily: "'Cinzel', 'Playfair Display', Georgia, serif" }}>
            Happy DIWALI to all NS family
          </h2>
          <DiyaIcon className="w-6 h-6 sm:w-7 sm:h-7 filter drop-shadow-[0_0_12px_rgba(245,158,11,0.95)]" />
        </div>

        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-ping" />
          <span className="text-[11px] font-bold tracking-wider text-amber-300 uppercase hidden sm:inline-block">
            Shubh Deepavali
          </span>
        </div>
      </header>

      {/* Main Split Layout */}
      <div className="flex-1 flex w-full relative">

        <div className="w-full lg:w-[560px] xl:w-[600px] flex flex-col justify-center px-4 sm:px-12 md:px-16 py-6 sm:py-8 z-20 relative shrink-0">
          
          <div 
            ref={cardRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{
              transform: `perspective(1100px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
              transition: 'transform 0.15s ease-out'
            }}
            className="relative rounded-[2rem] sm:rounded-[2.5rem] bg-gradient-to-b from-[#251307]/95 via-[#180B03]/95 to-[#0F0602]/95 p-6 sm:p-10 border-2 border-amber-400/50 shadow-[0_25px_80px_rgba(245,158,11,0.3),inset_0_1px_2px_rgba(254,240,138,0.4)] backdrop-blur-2xl overflow-hidden"
          >
            {/* Golden Akash Kandil Lantern Accent */}
            <div className="absolute top-2 right-4 sm:right-6 z-20 pointer-events-none drop-shadow-[0_6px_14px_rgba(245,158,11,0.6)]">
              <KandilIcon className="w-10 h-10 sm:w-14 sm:h-14 opacity-95 animate-pulse" />
            </div>

            {/* Corner Golden Decorative Rings */}
            <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full border border-amber-400/25 pointer-events-none" />
            <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full border border-yellow-300/30 pointer-events-none" />
            <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full border border-amber-500/20 pointer-events-none" />

            {/* Dynamic Surface Metallic Highlight */}
            <div 
              className="absolute inset-0 rounded-[2rem] sm:rounded-[2.5rem] pointer-events-none opacity-40 transition-opacity duration-300"
              style={{
                background: `radial-gradient(circle 320px at ${tilt.shineX}% ${tilt.shineY}%, rgba(254,240,138,0.22), transparent)`
              }}
            />

            {/* Header / Brand */}
            <div className="relative z-10 mb-6 sm:mb-8">
              <div className="flex items-center justify-between mb-4 sm:mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-b from-amber-400/25 to-amber-950/70 border border-amber-400/40 shadow-[0_8px_25px_rgba(217,119,6,0.45),inset_0_2px_4px_rgba(255,255,255,0.3)] flex items-center justify-center p-2 backdrop-blur-md transform hover:scale-105 transition-transform duration-300">
                    <img 
                      src="/logo.png" 
                      alt="NS Group" 
                      className="w-full h-full object-contain filter drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)]"
                      onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                    />
                  </div>
                  <ElectricNSGroup />
                </div>
              </div>

              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight" style={{ fontFamily: 'Outfit' }}>
                Welcome back
              </h1>
            </div>

            {/* Feedback Banners */}
            {error && (
              <div className="relative z-10 bg-rose-500/10 border border-rose-500/30 text-rose-300 p-3.5 sm:p-4 rounded-2xl mb-5 text-xs sm:text-sm font-semibold flex items-start gap-3 shadow-[0_10px_25px_rgba(244,63,94,0.15)] animate-in fade-in slide-in-from-top-2" data-testid="auth-error">
                <AlertCircle className="w-5 h-5 shrink-0 text-rose-400 mt-0.5" />
                <p className="leading-snug">{error}</p>
              </div>
            )}
            {success && (
              <div className="relative z-10 bg-amber-500/20 border border-amber-400/40 text-amber-200 p-3.5 sm:p-4 rounded-2xl mb-5 text-xs sm:text-sm font-semibold flex items-start gap-3 shadow-[0_10px_25px_rgba(245,158,11,0.2)] animate-in fade-in slide-in-from-top-2" data-testid="auth-success">
                <CheckCircle className="w-5 h-5 shrink-0 text-amber-300 mt-0.5" />
                <p className="leading-snug">{success}</p>
              </div>
            )}

            {/* Login Inputs */}
            <form onSubmit={handleLoginSubmit} className="relative z-10 space-y-4 sm:space-y-5" data-testid="login-form">
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-200/90 ml-1 flex items-center justify-between">
                  <span>Account Username</span>
                </label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-300 group-focus-within:text-amber-200 group-focus-within:border-amber-400/50 transition-all pointer-events-none">
                    <User className="w-4 h-4" />
                  </div>
                  <input 
                    type="text"
                    placeholder="Enter username or ID"
                    value={loginData.username}
                    onChange={e => setLoginData({...loginData, username: e.target.value})}
                    required
                    data-testid="login-username"
                    className="w-full bg-[#160B04]/90 border border-amber-500/35 rounded-2xl py-3.5 sm:py-4 pl-16 pr-4 text-sm font-semibold text-white placeholder:text-amber-300/40 shadow-[inset_0_2px_4px_rgba(0,0,0,0.8),0_1px_1px_rgba(255,255,255,0.05)] focus:outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-500/20 transition-all"
                  />
                </div>
              </div>

              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-200/90 ml-1">
                  Password
                </label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-300 group-focus-within:text-amber-200 group-focus-within:border-amber-400/50 transition-all pointer-events-none">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input 
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter security password"
                    value={loginData.password}
                    onChange={e => setLoginData({...loginData, password: e.target.value})}
                    required
                    data-testid="login-password"
                    className="w-full bg-[#160B04]/90 border border-amber-500/35 rounded-2xl py-3.5 sm:py-4 pl-16 pr-14 text-sm font-semibold text-white placeholder:text-amber-300/40 shadow-[inset_0_2px_4px_rgba(0,0,0,0.8),0_1px_1px_rgba(255,255,255,0.05)] focus:outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-500/20 transition-all"
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 p-2 rounded-xl text-amber-300/60 hover:text-white hover:bg-amber-500/10 transition-all cursor-pointer"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button 
                type="submit" 
                disabled={isSubmitDisabled}
                data-testid="login-submit"
                className="w-full mt-5 sm:mt-6 py-4 px-6 rounded-2xl bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 text-slate-950 font-black text-xs uppercase tracking-[0.2em] shadow-[0_12px_35px_rgba(245,158,11,0.45),inset_0_1px_2px_rgba(255,255,255,0.8),inset_0_-3px_0_rgba(180,83,9,0.4)] border-b-2 border-amber-700 hover:shadow-[0_18px_45px_rgba(245,158,11,0.65)] active:translate-y-1 active:border-b-0 active:shadow-[0_4px_15px_rgba(245,158,11,0.3)] transition-all flex items-center justify-center gap-2 group cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isFetchingGps ? (
                  <>
                    <MapPin className="w-4 h-4 animate-bounce text-slate-950" />
                    <span>Getting your location...</span>
                  </>
                ) : loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-slate-950" />
                    <span>Securing Credentials...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-300 text-slate-950" />
                  </>
                )}
              </button>
            </form>

            <div className="relative z-10 mt-5 sm:mt-6 text-center">
              <p className="text-[10px] font-bold text-amber-300/70 uppercase tracking-[0.25em]">
                NS GROUP &copy; 2026 &bull; ALL RIGHTS RESERVED
              </p>
            </div>
          </div>
        </div>


        <div className="hidden lg:flex flex-1 relative flex-col items-center justify-end pb-8 sm:pb-12 px-8 overflow-hidden bg-[#070402] z-0">
          
        
          <img 
            src="/login-bg.jpg" 
            alt="NS Group Capabilities Overview" 
            className="absolute inset-0 w-full h-full object-contain xl:object-cover opacity-100 brightness-110 contrast-110 saturate-105 filter drop-shadow-[0_0_25px_rgba(0,0,0,0.5)] transition-all duration-300 z-0" 
          />
         
          <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[#0B0602]/50 to-transparent pointer-events-none z-10" />
          <div className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-[#0B0602]/70 via-[#0B0602]/20 to-transparent pointer-events-none z-10" />
          
      
          <div className="relative z-20 max-w-lg w-full text-center space-y-2 p-4 sm:p-5 rounded-3xl bg-black/65 border border-amber-400/40 backdrop-blur-md shadow-[0_15px_40px_rgba(0,0,0,0.6)] -translate-x-12">
            <div className="flex justify-center -mt-0.5">
              <DiyaIcon className="w-9 h-9 filter drop-shadow-[0_0_14px_rgba(245,158,11,0.95)] animate-bounce duration-[3000ms]" />
            </div>
            <h3 className="text-lg sm:text-xl font-serif font-bold text-amber-200 tracking-wide drop-shadow-[0_2px_8px_rgba(245,158,11,0.5)]">
              Wishing You Prosperity, Joy & Illumination
            </h3>
            <p className="text-amber-100/90 text-xs leading-relaxed max-w-md mx-auto">
              May the divine light of Diwali bring happiness, peace, and bright success to all our endeavors across power, energy, and global automation.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};


const DynamicFireworks = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let rockets = [];
    let particles = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const colorPalettes = [
      ['#FFD700', '#F59E0B', '#FCD34D', '#FFFBEB'],
      ['#FF7700', '#FFD700', '#FF3300', '#FEF08A'],
      ['#F43F5E', '#F59E0B', '#FDE047', '#FFFFFF'],
      ['#00F0FF', '#FFD700', '#38BDF8', '#FFFFFF'],
    ];

    const styles = ['willow', 'double_ring', 'peony', 'comet'];

    class Rocket {
      constructor() {
        this.x = Math.random() * (canvas.width * 0.88) + canvas.width * 0.06;
        this.y = canvas.height;
        this.targetY = (Math.random() * 0.50 + 0.10) * canvas.height;
        this.speed = Math.random() * 4 + 11;
        this.angle = (Math.random() * 16 - 8) * (Math.PI / 180);
        this.vx = Math.sin(this.angle) * 3;
        this.vy = -this.speed;
        
        this.palette = colorPalettes[Math.floor(Math.random() * colorPalettes.length)];
        this.burstStyle = styles[Math.floor(Math.random() * styles.length)];
        this.exploded = false;
        this.trail = [];
      }

      update() {
        this.trail.push({ x: this.x, y: this.y });
        if (this.trail.length > 6) this.trail.shift();

        this.x += this.vx;
        this.y += this.vy;

        if (this.y <= this.targetY || this.vy >= -0.5) {
          this.exploded = true;
          this.explode();
        }
      }

      draw() {
        ctx.save();
        ctx.beginPath();
        for (let i = 0; i < this.trail.length; i++) {
          const pt = this.trail[i];
          if (i === 0) ctx.moveTo(pt.x, pt.y);
          else ctx.lineTo(pt.x, pt.y);
        }
        ctx.strokeStyle = this.palette[0];
        ctx.lineWidth = 2.5;
        ctx.shadowBlur = 10;
        ctx.shadowColor = this.palette[1];
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(this.x, this.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = '#FFFFFF';
        ctx.fill();
        ctx.restore();
      }

      explode() {
        const count = 85;

        if (this.burstStyle === 'double_ring') {
          for (let i = 0; i < count; i++) {
            const angle = (i / count) * Math.PI * 2;
            const speed = (i % 2 === 0 ? 7.5 : 4);
            particles.push(new Particle(this.x, this.y, Math.cos(angle) * speed, Math.sin(angle) * speed, this.palette, 'ring'));
          }
        } else if (this.burstStyle === 'willow') {
          for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = Math.random() * 7 + 1.5;
            particles.push(new Particle(this.x, this.y, Math.cos(angle) * speed, Math.sin(angle) * speed, ['#FFD700', '#FCD34D', '#FFFFFF'], 'willow'));
          }
        } else if (this.burstStyle === 'comet') {
          for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = Math.random() * 8.5 + 2;
            particles.push(new Particle(this.x, this.y, Math.cos(angle) * speed, Math.sin(angle) * speed, this.palette, 'comet'));
          }
        } else {
          for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = Math.random() * 8 + 1;
            particles.push(new Particle(this.x, this.y, Math.cos(angle) * speed, Math.sin(angle) * speed, this.palette, 'peony'));
          }
        }
      }
    }

    class Particle {
      constructor(x, y, vx, vy, palette, style) {
        this.x = x;
        this.y = y;
        this.vx = vx;
        this.vy = vy;
        this.style = style;
        this.alpha = 1;
        this.palette = palette;
        this.color = palette[Math.floor(Math.random() * palette.length)];
        
        if (style === 'willow') {
          this.friction = 0.965;
          this.gravity = 0.055;
          this.decay = Math.random() * 0.012 + 0.009;
          this.size = Math.random() * 2.5 + 1.2;
        } else if (style === 'comet') {
          this.friction = 0.955;
          this.gravity = 0.045;
          this.decay = Math.random() * 0.016 + 0.012;
          this.size = Math.random() * 3 + 1.8;
        } else {
          this.friction = 0.96;
          this.gravity = 0.04;
          this.decay = Math.random() * 0.018 + 0.013;
          this.size = Math.random() * 2.5 + 1.2;
        }

        this.trail = [];
      }

      update() {
        if (this.style === 'comet' || this.style === 'willow') {
          this.trail.push({ x: this.x, y: this.y });
          if (this.trail.length > 3) this.trail.shift();
        }

        this.vx *= this.friction;
        this.vy *= this.friction;
        this.vy += this.gravity;
        this.x += this.vx;
        this.y += this.vy;
        this.alpha -= this.decay;
      }

      draw() {
        ctx.save();
        ctx.globalAlpha = Math.max(0, this.alpha);

        if (this.style === 'willow' && Math.random() > 0.45) {
          ctx.fillStyle = '#FFFFFF';
        } else {
          ctx.fillStyle = this.color;
        }

        if (this.trail.length > 0) {
          ctx.beginPath();
          for (let i = 0; i < this.trail.length; i++) {
            if (i === 0) ctx.moveTo(this.trail[i].x, this.trail[i].y);
            else ctx.lineTo(this.trail[i].x, this.trail[i].y);
          }
          ctx.strokeStyle = this.color;
          ctx.lineWidth = this.size;
          ctx.stroke();
        }

        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.shadowBlur = 12;
        ctx.shadowColor = this.color;
        ctx.fill();
        ctx.restore();
      }
    }

    let lastLaunch = 0;
    const animate = (timestamp) => {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0, 0, 0, 0.16)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'lighter';

      if (rockets.length === 0 && particles.length === 0) {
        if (!lastLaunch || timestamp - lastLaunch >= 350) {
          rockets.push(new Rocket());
          rockets.push(new Rocket());
          lastLaunch = timestamp;
        }
      }

      for (let i = rockets.length - 1; i >= 0; i--) {
        rockets[i].update();
        if (!rockets[i].exploded) {
          rockets[i].draw();
        } else {
          rockets.splice(i, 1);
        }
      }

      for (let i = particles.length - 1; i >= 0; i--) {
        particles[i].update();
        if (particles[i].alpha > 0) {
          particles[i].draw();
        } else {
          particles.splice(i, 1);
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas 
      ref={canvasRef} 
      className="fixed inset-0 pointer-events-none z-10 w-full h-full" 
    />
  );
};

// Sub-Component: Electric Branding Badge
const ElectricNSGroup = () => (
  <div className="relative inline-flex flex-col select-none py-2 px-1">
    <div className="relative inline-block">
      <div 
        className="absolute inset-0 -m-2 rounded-full pointer-events-none blur-lg"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(245,158,11,0.4) 0%, rgba(253,224,71,0.2) 45%, transparent 75%)',
          animation: 'empRipple 2.2s ease-out infinite',
        }}
      />

      <span
        className="absolute inset-0 text-3xl font-black tracking-tight block leading-none pointer-events-none blur-[3px] select-none text-[#F59E0B] opacity-80 animate-voltage-tremor"
        style={{ fontFamily: 'Outfit' }}
        aria-hidden="true"
      >
        NS Group
      </span>

      <span
        className="relative text-3xl font-black tracking-tight block leading-none select-none electric-gradient-text drop-shadow-[0_0_8px_rgba(245,158,11,0.9)]"
        style={{ fontFamily: 'Outfit' }}
        data-testid="login-title"
      >
        NS Group
      </span>

      <svg
        className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
        viewBox="0 0 200 40"
        preserveAspectRatio="none"
      >
        <defs>
          <filter id="electric-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <path
          d="M -10 20 Q 25 2, 60 20 T 130 20 T 210 20"
          fill="none"
          stroke="#F59E0B"
          strokeWidth="2.2"
          strokeLinecap="round"
          filter="url(#electric-glow)"
          className="animate-electric-surge"
        />

        <g className="animate-plasma-wave">
          <path
            d="M -20 22 Q 15 34, 50 22 T 120 22 T 190 22 T 230 22"
            fill="none"
            stroke="#FDE047"
            strokeWidth="1.4"
            strokeDasharray="15 25"
            opacity="0.85"
            filter="url(#electric-glow)"
          />
        </g>

        <path
          d="M 12 10 L 22 22 L 18 25 L 32 36 M 142 8 L 150 18 L 145 22 L 160 32"
          fill="none"
          stroke="#ffffff"
          strokeWidth="1.8"
          strokeLinecap="round"
          filter="url(#electric-glow)"
          className="animate-lightning"
        />
      </svg>
    </div>

    <span className="text-[10px] font-extrabold uppercase tracking-[0.25em] text-[#FCD34D] mt-1.5 block drop-shadow-[0_0_10px_rgba(245,158,11,0.6)] animate-pulse">
      Personnel Attendance Portal
    </span>
  </div>
);


// Diwali Golden Akash Kandil (Lantern)
const KandilIcon = ({ className = "w-12 h-12" }) => (
  <svg viewBox="0 0 64 64" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <line x1="32" y1="0" x2="32" y2="12" stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="2 2" />
    <polygon points="32,12 48,28 32,44 16,28" fill="url(#kandilGoldGrad)" stroke="#FDE047" strokeWidth="1.5" />
    <polygon points="32,17 43,28 32,39 21,28" fill="none" stroke="#FEF08A" strokeWidth="1" opacity="0.8" />
    <circle cx="32" cy="28" r="5" fill="#FEF08A" className="animate-pulse" />
    <path d="M20 44 L16 62 M26 44 L24 64 M32 44 L32 64 M38 44 L40 64 M44 44 L48 62" stroke="#F59E0B" strokeWidth="1.8" strokeLinecap="round" />
    <defs>
      <linearGradient id="kandilGoldGrad" x1="16" y1="12" x2="48" y2="44" gradientUnits="userSpaceOnUse">
        <stop stopColor="#D97706" />
        <stop offset="0.5" stopColor="#F59E0B" />
        <stop offset="1" stopColor="#78350F" />
      </linearGradient>
    </defs>
  </svg>
);

// Diwali Glowing Diya Icon
const DiyaIcon = ({ className = "w-8 h-8" }) => (
  <svg viewBox="0 0 64 48" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M6 24C6 37.25 17.65 44 32 44C46.35 44 58 37.25 58 24C46 27 18 27 6 24Z" 
      fill="url(#diyaPureGold)" 
      stroke="#F59E0B" 
      strokeWidth="1.5" 
    />
    <path d="M12 25C20 28 44 28 52 25" stroke="#FEF08A" strokeWidth="1" strokeDasharray="2 2" />
    <path 
      d="M32 3C32 3 24 13 24 20C24 24.4 27.6 28 32 28C36.4 28 40 24.4 40 20C40 13 32 3 32 3Z" 
      fill="url(#goldFlameGrad)" 
      className="animate-pulse origin-bottom" 
    />
    <path d="M32 9C32 9 27 15 27 19.5C27 22.2 29.2 24.5 32 24.5C34.8 24.5 37 22.2 37 19.5C37 15 32 9 32 9Z" fill="#FFFBEB" />
    <defs>
      <linearGradient id="diyaPureGold" x1="6" y1="24" x2="58" y2="44" gradientUnits="userSpaceOnUse">
        <stop stopColor="#B45309" />
        <stop offset="0.5" stopColor="#F59E0B" />
        <stop offset="1" stopColor="#FCD34D" />
      </linearGradient>
      <linearGradient id="goldFlameGrad" x1="32" y1="3" x2="32" y2="28" gradientUnits="userSpaceOnUse">
        <stop stopColor="#FFFBEB" />
        <stop offset="0.4" stopColor="#F59E0B" />
        <stop offset="1" stopColor="#D97706" />
      </linearGradient>
    </defs>
  </svg>
);


const STYLE_SHEET = `
  @keyframes electricSurge {
    0% { stroke-dashoffset: 600; opacity: 0.7; }
    50% { stroke-dashoffset: 0; opacity: 1; filter: drop-shadow(0 0 8px #F59E0B) drop-shadow(0 0 16px #FCD34D); }
    100% { stroke-dashoffset: -600; opacity: 0.7; }
  }
  @keyframes plasmaSine {
    0% { transform: translateX(-25%) scaleY(1); }
    50% { transform: translateX(0%) scaleY(1.35) skewX(-2deg); }
    100% { transform: translateX(-25%) scaleY(1); }
  }
  @keyframes lightningStrike {
    0%, 92%, 100% { opacity: 0; transform: scale(0.95); }
    93% { opacity: 1; transform: scale(1.08) translate(1px, -1px); }
    95% { opacity: 0.2; }
    96% { opacity: 0.95; transform: scale(1.04) translate(-1px, 1px); }
    98% { opacity: 0; }
  }
  @keyframes highVoltageTremor {
    0%, 100% { transform: translate(0, 0); }
    20% { transform: translate(-0.8px, 0.6px); }
    40% { transform: translate(0.6px, -0.6px); }
    60% { transform: translate(-0.5px, -0.5px); }
    80% { transform: translate(0.8px, 0.4px); }
  }
  @keyframes currentPass {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  @keyframes empRipple {
    0% { transform: scaleX(0.7) scaleY(0.4); opacity: 0.9; }
    70% { transform: scaleX(1.4) scaleY(1.8); opacity: 0.3; }
    100% { transform: scaleX(1.7) scaleY(2.2); opacity: 0; }
  }
  .animate-electric-surge {
    stroke-dasharray: 120 180;
    animation: electricSurge 2.2s linear infinite;
  }
  .animate-plasma-wave {
    animation: plasmaSine 1.8s ease-in-out infinite;
  }
  .animate-lightning {
    animation: lightningStrike 2.5s infinite;
  }
  .animate-voltage-tremor {
    animation: highVoltageTremor 0.12s infinite;
  }
  .electric-gradient-text {
    background: linear-gradient(
      90deg,
      #ffffff 0%,
      #fde047 20%,
      #f59e0b 45%,
      #ffffff 55%,
      #fbbf24 80%,
      #ffffff 100%
    );
    background-size: 250% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: currentPass 2.4s linear infinite;
  }
`;

export default LoginForm;